import React, { useState } from 'react';
import { validateSingle } from '../api/client';
import StatusBadge from './StatusBadge';

const EXAMPLE = {
  freitext: 'Schmerzen im linken Knie',
  code: 'M25.5',
};

export default function SingleValidator({ onResult }) {
  const [freitext, setFreitext] = useState('');
  const [code, setCode]         = useState('');
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  const loadExample = () => {
    setFreitext(EXAMPLE.freitext);
    setCode(EXAMPLE.code);
    setResult(null);
    setError('');
  };

  const run = async () => {
    if (!freitext.trim() || !code.trim()) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const data = await validateSingle(freitext.trim(), code.trim());
      setResult(data);
      onResult?.(data);
    } catch (e) {
      setError(e.response?.data?.detail || 'API nicht erreichbar — läuft der Server?');
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => { if (e.key === 'Enter') run(); };

  return (
    <div style={{ animation: 'fadeUp 0.4s ease' }}>
      {/* Disclaimer */}
      <div style={{
        marginBottom: 20, padding: '10px 14px',
        background: 'rgba(74,240,196,0.05)', border: '1px solid rgba(74,240,196,0.2)',
        borderRadius: 8, fontSize: 12, color: 'var(--text2)', lineHeight: 1.6,
      }}>
        Dieses Tool unterstützt nur die <strong style={{ color: 'var(--text)' }}>Plausibilitätsprüfung</strong> von ICD-10-Codes.
        Es ersetzt keine medizinische oder abrechnungsrechtliche Prüfung. Manuelle Kontrolle bleibt erforderlich.
      </div>

      {/* Inputs */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 200px', gap: 12, marginBottom: 12 }}>
        <div>
          <label style={labelStyle}>Freitext-Diagnose</label>
          <input
            value={freitext}
            onChange={e => setFreitext(e.target.value)}
            onKeyDown={handleKey}
            placeholder="z.B. Schmerzen im linken Knie"
          />
        </div>
        <div>
          <label style={labelStyle}>ICD-10 Code</label>
          <input
            value={code}
            onChange={e => setCode(e.target.value)}
            onKeyDown={handleKey}
            placeholder="z.B. M25.5"
            style={{ fontFamily: 'Consolas, monospace', letterSpacing: '0.06em' }}
          />
        </div>
      </div>

      {/* Buttons */}
      <div style={{ display: 'flex', gap: 10 }}>
        <button onClick={run} disabled={loading || !freitext || !code} style={btnStyle(loading)}>
          {loading ? 'Prüfe...' : 'Diagnose validieren →'}
        </button>
        <button onClick={loadExample} style={exampleBtnStyle}>
          Beispieldaten laden
        </button>
      </div>

      {error && (
        <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--error-bg)', border: '1px solid var(--error-border)', borderRadius: 8, color: 'var(--error)', fontSize: 13 }}>
          {error}
        </div>
      )}

      {result && <ResultCard result={result} />}
    </div>
  );
}

function ResultCard({ result }) {
  const statusColors = {
    ok:        'var(--ok-border)',
    warning:   'var(--warn-border)',
    error:     'var(--error-border)',
    uncertain: 'var(--border2)',
  };
  const borderColor = statusColors[result.status] || 'var(--border2)';

  return (
    <div style={{
      marginTop: 20, padding: '20px 24px',
      background: 'var(--surface)', border: `1px solid ${borderColor}`,
      borderRadius: 12, animation: 'fadeUp 0.3s ease',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <StatusBadge status={result.status} />
          <span style={{ fontSize: 13, color: 'var(--text2)' }}>
            Manuelle Prüfung {result.manual_review_required ? 'erforderlich' : 'nicht nötig'}
          </span>
        </div>
        <span style={{ fontFamily: 'Consolas, monospace', fontSize: 13, color: 'var(--text2)' }}>
          {Math.round(result.confidence * 100)}% Übereinstimmung
        </span>
      </div>

      {/* Info-Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <InfoRow label="Freitext" value={result.freitext} />
        <InfoRow label="ICD-10 Code" value={result.icd_code} mono />
        <InfoRow
          label="Code bedeutet"
          value={result.icd_desc}
          highlight={result.status === 'error' || result.status === 'warning'}
        />
        {result.matched_keywords?.length > 0 && (
          <InfoRow label="Treffer-Keywords" value={result.matched_keywords.join(', ')} />
        )}
      </div>

      {/* Ergebnis-Nachricht */}
      {result.message && (
        <div style={{ marginTop: 14, padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8, fontSize: 13, color: 'var(--text2)', borderLeft: `3px solid ${borderColor}` }}>
          {result.message}
        </div>
      )}

      {/* Begründungen */}
      {result.reasons?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <p style={sectionLabel}>Begründungen</p>
          <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {result.reasons.map((reason, i) => (
              <li key={i} style={{
                display: 'flex', gap: 8, alignItems: 'flex-start',
                padding: '7px 12px', background: 'var(--surface2)',
                borderRadius: 7, fontSize: 12, color: 'var(--text2)',
              }}>
                <span style={{ color: 'var(--text3)', flexShrink: 0, marginTop: 1 }}>→</span>
                {reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Vorschläge */}
      {result.suggestions?.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <p style={sectionLabel}>Vorschläge</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {result.suggestions.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--surface2)', borderRadius: 8, border: '1px solid var(--border)' }}>
                <span style={{ fontFamily: 'Consolas, monospace', fontSize: 13, color: 'var(--accent)', minWidth: 70 }}>{s.code}</span>
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>{s.desc}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function InfoRow({ label, value, mono, highlight }) {
  return (
    <div style={{ padding: '10px 14px', background: 'var(--surface2)', borderRadius: 8 }}>
      <p style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'Arial, sans-serif', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{label}</p>
      <p style={{ fontSize: 14, color: highlight ? 'var(--warn)' : 'var(--text)', fontFamily: mono ? 'Consolas, monospace' : 'inherit', letterSpacing: mono ? '0.06em' : 'normal' }}>{value}</p>
    </div>
  );
}

const sectionLabel = {
  fontSize: 11, color: 'var(--text3)', fontFamily: 'Arial, sans-serif',
  fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8,
};

const labelStyle = {
  display: 'block', fontSize: 11, color: 'var(--text3)',
  fontFamily: 'Arial, sans-serif', fontWeight: 600,
  letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 7,
};

const btnStyle = (loading) => ({
  padding: '11px 24px',
  background: loading ? 'var(--surface2)' : 'var(--accent)',
  color: loading ? 'var(--text3)' : '#0a1a14',
  fontSize: 13, fontWeight: 700, letterSpacing: '0.04em',
  cursor: loading ? 'not-allowed' : 'pointer',
  opacity: loading ? 0.7 : 1,
});

const exampleBtnStyle = {
  padding: '11px 20px',
  background: 'var(--surface)',
  border: '1px solid var(--border)',
  color: 'var(--text2)',
  fontSize: 13, fontWeight: 600,
};
